/**
 * The texts and the row types of the warning before a date change (decision 16, mock screen 1). This file imports no
 * generated runtime code, so the error normalizer and the components can use it in any test.
 */

/** The error code of the 409 answer. */
export const DEADLINE_CHANGE_REQUIRES_CONFIRMATION = "DEADLINE_CHANGE_REQUIRES_CONFIRMATION";

export const DEADLINE_CHANGE_TITLE = "This save changes the due dates";
export const DEADLINE_CHANGE_DESCRIPTION =
  "Check the changes. Then save, or do not save. If you do not save, the system saves nothing.";
export const DEADLINE_CHANGE_STALE_TITLE = "The due dates changed again";
export const DEADLINE_CHANGE_STALE_TEXT =
  "The facts or the rules changed after the first check. The table shows the new values. Check them again.";
export const DEADLINE_CHANGE_YOUR_CHANGES = "Your changes";
export const DEADLINE_CHANGE_COLUMNS = { dueDate: "Due date", now: "Now", after: "After the save", why: "Why" } as const;
export const DEADLINE_CHANGE_EARLIEST_LABEL = "Earliest internal date";
export const DEADLINE_CHANGE_EARLIEST_SCOPE = "(header and work queue):";
export const DEADLINE_CHANGE_HISTORY_NOTE = "The history records your name and that you confirmed the change.";
export const DEADLINE_CHANGE_DECLINE = "Do not save";
export const DEADLINE_CHANGE_APPROVE = "Save and change the due dates";
export const DEADLINE_CHANGE_SAVING = "Saving…";

export const DEADLINE_CHANGE_SAVED_TITLE = "Saved. The due dates changed.";
/** "The earliest internal date is now 13 Oct 2026. The history records your confirmation." */
export const deadlineChangeSavedText = (earliest: string): string =>
  `The earliest internal date is now ${earliest}. The history records your confirmation.`;

export const DEADLINE_CHANGE_NOT_SAVED_TITLE = "Not saved. The system saved none of your changes.";
/** "You did not approve the change of the due dates. These 2 changes are not in the case:" */
export const deadlineChangeNotSavedIntro = (count: number): string =>
  `You did not approve the change of the due dates. These ${count} ${count === 1 ? "change is" : "changes are"} not in the case:`;
export const DEADLINE_CHANGE_NOT_SAVED_OUTRO =
  "The due dates did not change. Your changes stay on the screen. Correct them and click Save again, or discard them.";
export const DEADLINE_CHANGE_SHOW_FIELDS = "Show the fields";
export const DEADLINE_CHANGE_DISCARD = "Discard my changes";
export const DEADLINE_CHANGE_CLOSE = "Close";
export const DEADLINE_CHANGE_LAST_SAVE_FAILED = "The last save did not complete.";
export const NO_UNSAVED_CHANGES = "No unsaved changes";
/** The footer count: "2 unsaved fields". */
export const unsavedCountText = (count: number): string => `${count} unsaved ${count === 1 ? "field" : "fields"}`;
/** The footer while the banner shows: "2 unsaved fields · not saved". */
export const unsavedNotSavedText = (count: number): string =>
  `${count} unsaved ${count === 1 ? "field" : "fields"} · not saved`;
/** "Seriousness: the case keeps Non-serious. Your unsaved value is Serious." */
export const notSavedFieldText = { keeps: "the case keeps", unsaved: "Your unsaved value is" } as const;

/** A field or a priority without a value. */
export const NO_VALUE = "—";
/** A date that does not exist, for example no earliest internal date. */
export const NO_DATE = "None";
export const NO_DUE_DATE = "No due date";
export const NO_REPORT = "No report";

/** The words of the table rows. */
export const DEADLINE_CHANGE_ROW_TEXT = {
  priority: "Priority",
  case: "Case",
  priorityWhy: "The priority comes from the seriousness and the listedness of the case.",
  internalWork: "Internal work date",
  initialReport: "Initial report",
  followupReport: "Follow-up report",
  complete: "Complete",
  notApplicable: "Not applicable",
  withdrawn: "Withdrawn",
  pending: "Pending",
  pendingWhy: "The system waits for missing facts.",
  notApplicableWhy: "The rule starts no report.",
  newBadge: "new",
} as const;
/** "EMA · Expedited report", or "EMA · Correction of expedited report". */
export const authorityRowLabel = (authority: string, kind: string, correction: boolean): string =>
  `${authority} · ${correction ? `Correction of ${kind.toLowerCase()}` : kind}`;
/** "Regulatory 16 Oct 2026" and "Internal 13 Oct 2026". */
export const regulatoryLine = (date: string): string => `Regulatory ${date}`;
export const internalLine = (date: string): string => `Internal ${date}`;
/** "15 days from the receipt date 01 Oct 2026.", with "Priority High: " before it for internal work. */
export const ruleWhyText = (days: number, start: string, priority?: string): string =>
  `${priority ? `Priority ${priority}: ` : ""}${days} days from the receipt date ${start}.`;
export const changedInputsWhy = (labels: string[]): string => `Changed: ${labels.join(", ")}.`;
/** "15 days earlier" or "1 day later". */
export const moveBadgeText = (days: number, earlier: boolean): string =>
  `${days} ${days === 1 ? "day" : "days"} ${earlier ? "earlier" : "later"}`;

/** One field that the user changed and the server did not save. */
export interface UnsavedFieldChange {
  /** Where the field is, for "Show the fields". */
  fieldId: string;
  recordId?: string;
  /** "Event 1 · Seriousness". */
  label: string;
  savedValue: string;
  unsavedValue: string;
}

/** The size and direction of a date move, or a result that gets its first date. */
export interface DeadlineChangeBadge {
  text: string;
  tone: "earlier" | "later" | "new";
}

/** One row of the table "Due date / Now / After the save / Why". */
export interface DeadlineChangeRow {
  key: string;
  label: string;
  detail: string;
  now: string[];
  after: string[];
  badge?: DeadlineChangeBadge;
  why: string;
  whyDetail?: string;
}

/** Everything the dialog shows, ready to render. */
export interface DeadlineChangeSummary {
  rows: DeadlineChangeRow[];
  earliestBefore: string;
  earliestAfter: string;
}
